import axios from 'axios'
import Cookies from 'js-cookie'
import isPlainObject from 'lodash/isPlainObject'

export const axiosMarxy = axios.create({
  baseURL: (() => {
    const marxyApiUrl = 'https://mg-marxy.mall.cz'
    if (typeof marxyApiUrl !== 'string') {
      console.warn('Marxy API url does not exist!')

      return
    }

    return marxyApiUrl
  })(),
})

axiosMarxy.defaults.timeout = 2000

const delay = (duration: number) =>
  new Promise((resolve) => setTimeout(resolve, duration))

const OK = 200
const INTERNAL_SERVER_ERROR = 500
const TOO_MANY_REQUESTS = 429
const RETRYER_CONFIG_DEFAULT = {
  maxAttempts: 10,
  maxDelayCoefficient: 5000,
}

// Retry request on axios error. Even if get network or client error (ECONNABORTED,ECONNREFUSED, ...).
// List of known errors is described at https://confluence.mallgroup.com/display/MSB/Marxy
export async function axiosMarxyRetryer(
  axiosInvokeFn: Function,
  customConfig = {} as any
): Promise<any> {
  try {
    return await axiosInvokeFn()
  } catch (err) {
    if (!err.isAxiosError) throw err
    // 1xx - Retry (Informational, request still processing)
    // 2xx - Do not retry (Success)
    // 3xx - Do not retry (Redirect)
    // 4xx - Do not retry (Client errors)
    // 429 - Retry ("Too Many Requests")
    // 5xx - Retry (Server errors)
    if (err.response && err.response.status) {
      const statusCode = err.response.status
      if (
        statusCode >= OK &&
        statusCode < INTERNAL_SERVER_ERROR &&
        statusCode !== TOO_MANY_REQUESTS
      ) {
        err.message = `Axios retryer was not used because a client error was received [${err.message}]`
        throw err
      }
    }

    const retryerConfig = {
      ...RETRYER_CONFIG_DEFAULT,
      ...customConfig,
      _attempt: customConfig._attempt || 1,
    }

    if (retryerConfig._attempt >= retryerConfig.maxAttempts) {
      err.message = `Axios retryer failed after [${retryerConfig.maxAttempts}] attempts because [${err.message}]`
      throw err
    }
    await delay(
      retryerConfig.maxDelayCoefficient /
        (retryerConfig.maxAttempts - retryerConfig._attempt)
    )

    await axiosMarxyRetryer(axiosInvokeFn, {
      ...retryerConfig,
      _attempt: retryerConfig._attempt + 1,
    })
  }
}

export function encodeData(data: Object): string {
  return encodeURIComponent(btoa(encodeURIComponent(JSON.stringify(data))))
}

export function decodeData(val: string): Object {
  return JSON.parse(decodeURIComponent(atob(decodeURIComponent(val))))
}

/**
 * Marxy API Service
 */
export default class MarxyApiService {
  static trackGet(data: Object) {
    if (!data) {
      throw new Error('Argument data is required!')
    }

    if (!isPlainObject(data)) {
      throw new Error('Argument "data" must be a type of Object!')
    }

    return axiosMarxyRetryer(() =>
      axiosMarxy.get('/track', {
        headers: {
          'Content-Type': 'text/plain',
        },
        params: {
          _event: encodeData(data),
        },
      })
    )
  }

  static trackPost(data: Object) {
    if (!data) {
      throw new Error('Argument "data" is required!')
    }

    if (!isPlainObject(data)) {
      throw new Error('Argument "data" must be a type of Object!')
    }

    return axiosMarxyRetryer(() =>
      axiosMarxy.post('/track', data, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    )
  }

  static async apiLog(message: string, e = {} as any): Promise<void> {
    if (!message) {
      console.error('Marxy log: argument message is required!')

      return
    }

    const data = {
      errorParams: null,
      location: window.location.href,
      stackSplit:
        e.stack && e.stack.split('\n').map((row: string) => row.trim()),
      userAgent: window.navigator.userAgent,
      version: window.marxy?.version,
    }

    if (e.params) data.errorParams = e.params

    const msg = {
      message,
      ...data,
    }

    if (Cookies.get('_mgMarxyDebugMode') === '3') console.error(msg)

    try {
      await axiosMarxyRetryer(() =>
        axiosMarxy.get('/log', {
          params: {
            message: encodeData(msg),
          },
        })
      )
    } catch (err) {
      console.error(`Marxy log: Error in request [${err.message}]`)
    }
  }
}
